import React from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import NextRoom from "@/components/NextRoom";
import { ARTIFACTS, ARTIFACT_CATEGORIES, TEXTURES } from "@/data/content";
import { Medallion } from "@/components/CompassMark";

const PLACEHOLDERS = [TEXTURES.stairs, TEXTURES.buildingDetail, TEXTURES.corridor, TEXTURES.emptyRoom, TEXTURES.chairs];

export default function Artifacts() {
  return (
    <PageShell
      testId="page-artifacts"
      title="Artifacts | Quentin Young"
      description="A growing archive of early marks, drafts, concepts, projects, and working evidence that document the evolution of Quentin Young's body of work."
      ogImage="/og-artifacts.jpg"
      breadcrumbs={[{ name: "Home", url: "/" }, { name: "Artifacts", url: "/artifacts" }]}
    >
      <section className="container-edge pt-16 md:pt-24 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <p className="eyebrow-gold mb-5">Artifacts</p>
            <h1 className="display-serif text-4xl md:text-6xl lg:text-7xl text-paper-primary text-balance leading-[1.02]">
              The drafts that survived the wastebasket.
            </h1>
            <p className="mt-8 max-w-2xl font-serif text-lg md:text-xl text-paper-secondary leading-relaxed text-pretty">
              People trust evolution more than they trust perfection. The originals are kept on purpose.
              Placeholder scans stand in until the real artifacts arrive.
            </p>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <Medallion size={200} />
          </div>
        </div>
      </section>

      <section className="bg-ink-base border-t border-ink-line py-12 md:py-16">
        <div className="container-edge">
          <div className="flex items-baseline justify-between mb-10">
            <p className="eyebrow">Archive</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-paper-mute">
              {ARTIFACTS.length} artifact{ARTIFACTS.length === 1 ? "" : "s"} · {ARTIFACT_CATEGORIES.length} categories
            </p>
          </div>

          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink-line">
            {ARTIFACTS.map((a, i) => {
              const cat = ARTIFACT_CATEGORIES.find((c) => c.slug === a.category);
              const image = a.image || PLACEHOLDERS[i % PLACEHOLDERS.length];
              return (
                <li key={a.slug} className="bg-ink-base" data-testid={`artifact-${a.slug}`}>
                  <Link to={`/artifacts/${a.slug}`} className="group block overflow-hidden">
                    <div className="relative aspect-[4/3] overflow-hidden bg-ink-surface">
                      <div
                        className="absolute inset-0 grayscale opacity-60 group-hover:opacity-90 group-hover:scale-[1.02] transition-all duration-700"
                        style={{ backgroundImage: `url(${image})`, backgroundSize: "cover", backgroundPosition: "center" }}
                        aria-hidden="true"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-ink-base via-ink-base/40 to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4 flex items-baseline justify-between gap-3">
                        <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold/80">{cat?.name || a.category}</p>
                        <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-paper-mute">{a.era}</p>
                      </div>
                    </div>
                    <div className="p-8">
                      <h3 className="font-serif text-2xl text-paper-primary group-hover:text-gold-warm transition-colors duration-300">{a.title}</h3>
                      <p className="mt-3 text-paper-secondary text-sm leading-relaxed line-clamp-3">{a.context}</p>
                      <span className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-paper-tertiary group-hover:text-paper-primary">
                        Open the artifact <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                      </span>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>

          <p className="mt-12 text-paper-tertiary text-sm italic font-serif text-center max-w-2xl mx-auto">
            More artifacts will appear here as the archive earns them. Silence is preferable to filler.
          </p>
        </div>
      </section>

      <NextRoom
        eyebrow="Move to the next room"
        title="Evidence lives elsewhere too."
        rooms={[
          { label: "See what these became", to: "/building", hint: "proof in reality" },
          { label: "Read the related observations", to: "/thinking", hint: "thinking behind it" },
          { label: "Study the framework", to: "/philosophy", hint: "how it is evaluated" },
        ]}
        testId="artifacts-next"
      />
    </PageShell>
  );
}
